import { cilChevronBottom, cilSearch } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { CCol, CRow, CTooltip } from '@coreui/react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Tooltip } from '@mui/material';
import React, { ReactNode } from 'react';
import CustomSelectInput from '../InputsComponent/CustomSelectInput';
import Spinner from '../UI/Spinner';
import FilterIconMenu from './FilterIconMenu';

interface OtherControl {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  fn: () => void;
  title?: string;
  tooltip?: string;
}

interface DataGridHeaderProps {
  title: string;
  addButton?: string;
  addBtnClick?: () => void;
  otherControls?: OtherControl[];
  titleControls?: OtherControl[];
  addSecButton?: string;
  addSecBtnClick?: () => void;
  filterDisable?: boolean;
  wordLoading?: boolean;
  exportFn?: () => void;
  wordExportFn?: () => void;
  addBtnContent?: ReactNode;
  popOverId?: string;
  headerClassName?: string;
  actionCell?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actions?: any[];
  labelNote?: string;
  onTitleClick?: () => void;
  topSearch?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
  };
  topSelect?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any[];
  };
  pdfLoading?: boolean;
}

/** Reusable search + select inputs, rendered in both desktop (inline) and mobile (row below) */
const SearchSelectInputs: React.FC<Pick<DataGridHeaderProps, 'topSearch' | 'topSelect'>> = ({
  topSearch,
  topSelect,
}) => (
  <CRow className="g-2 justify-content-center w-100 m-0">
    {topSearch && (
      <CCol xs={12} sm={topSelect ? 6 : 12} className="d-flex justify-content-center p-0">
        <div className="position-relative w-100" style={{ maxWidth: '400px' }}>
          <input
            type="text"
            className="form-control top-search-input"
            placeholder={topSearch.placeholder || 'Search...'}
            value={topSearch.value}
            onChange={topSearch.onChange}
            style={{ borderRadius: '8px', fontSize: '0.9rem', paddingRight: '35px' }}
          />
          <CIcon
            icon={cilSearch}
            className="position-absolute"
            style={{
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.6,
              pointerEvents: 'none',
            }}
          />
        </div>
      </CCol>
    )}

    {topSelect && (
      <CCol xs={12} sm={topSearch ? 6 : 12} className="d-flex justify-content-center p-0">
        <div className="w-100" style={{ maxWidth: '400px' }}>
          <CustomSelectInput
            id="advance-select"
            onChange={topSelect?.onChange}
            value={topSelect?.value}
            options={topSelect?.data}
            className="form-control pe-5 top-search-input"
            disableOption="Select Advance Filter"
            width={'w-100'}
            showDisabled={true}
            icon={undefined}
            name={undefined}
            isRequired={undefined}
            disabled={undefined}
            title={undefined}
            message={undefined}
            label={undefined}
            onClick={undefined}
            optionsList={undefined}
          />
        </div>
      </CCol>
    )}
  </CRow>
);

const DataGridHeader: React.FC<DataGridHeaderProps> = ({
  title,
  addButton,
  addBtnClick,
  otherControls,
  addSecButton,
  addSecBtnClick,
  filterDisable,
  exportFn,
  wordExportFn,
  addBtnContent,
  popOverId,
  actionCell,
  actions,
  titleControls,
  onTitleClick,
  topSearch,
  wordLoading,
  topSelect,
  headerClassName,
  labelNote,
  pdfLoading,
}) => {
  const detectToggleInOC = otherControls?.find(
    (i) => JSON.stringify(i.icon) === JSON.stringify(cilChevronBottom),
  );

  const hasSearchOrSelect = !!(topSelect || topSearch);

  return (
    <div className={`d-flex flex-column w-100 ${headerClassName || ''}`}>
      {/* ── Single row on all screen sizes ── */}
      <CRow className="w-100 p-0 align-items-center position-relative g-2">
        {/* Left: Title */}
        <CCol
          xs={(otherControls || [])?.length <= 1 ? 6 : 4}
          lg={hasSearchOrSelect ? 3 : 6}
          className="d-flex align-items-center justify-content-between p-0"
        >
          {/* Left side */}
          <div
            className="pointer heading-font-weight"
            onClick={onTitleClick || detectToggleInOC?.fn || undefined}
          >
            {title}
          </div>

          {/* Right side of LEFT column (title controls) */}
          <div className="d-flex align-items-center">
            {titleControls && <span className="mx-2">|</span>}

            {titleControls &&
              titleControls.map(({ icon, fn, title }, index) => (
                <div key={index} className="d-flex flex-row gap-1 align-items-center ms-0">
                  {title && (
                    <p className="labelName heading-font-weight text-truncate m-0" onClick={fn}>
                      {title}
                    </p>
                  )}
                  <CIcon className="stock-toggle-icon" onClick={fn} icon={icon} />
                </div>
              ))}
          </div>
        </CCol>

        {/* TRUE CENTER LABEL NOTE */}
        {/* TRUE CENTER LABEL NOTE — desktop only (absolute overlay) */}
        {labelNote && (
          <div
            className="position-absolute w-100 text-center label-note-warning d-none d-lg-block"
            style={{ left: 0, pointerEvents: 'none' }}
          >
            {labelNote}
          </div>
        )}

        {/* Center: Search/Select — desktop only */}
        {hasSearchOrSelect && (
          <CCol xs={0} lg={6} className="d-none d-lg-flex align-items-center p-0">
            <SearchSelectInputs topSearch={topSearch} topSelect={topSelect} />
          </CCol>
        )}

        {/* Right: Actions + Exports */}
        <CCol
          xs={(otherControls || [])?.length <= 1 ? 6 : 8}
          lg={hasSearchOrSelect ? 3 : 6}
          className="d-flex justify-content-end align-items-center gap-3 p-0 flex-wrap"
        >
          {actionCell && actionCell}

          {actions &&
            actions.map((action, index) => (
              <span
                key={index}
                className="lblAddPartner labelName heading-font-weight text-truncate"
                onClick={action.onClick}
              >
                {action.title}
              </span>
            ))}

          {addButton && (
            <span
              className="lblAddPartner labelName heading-font-weight text-truncate"
              onClick={addBtnClick}
              aria-describedby={popOverId}
            >
              {addButton} {addBtnContent ? addBtnContent : '(+)'}
            </span>
          )}

          {addSecButton && (
            <span
              className="lblAddPartner labelName heading-font-weight text-truncate"
              onClick={addSecBtnClick}
            >
              {addSecButton}
            </span>
          )}

          {exportFn && (
            <Tooltip title="Download PDF Report">
              {pdfLoading ? (
                <Spinner />
              ) : (
                <FileDownloadOutlinedIcon
                  fontSize="medium"
                  className="stock-toggle-icon"
                  onClick={exportFn}
                />
              )}
            </Tooltip>
          )}

          {wordExportFn && (
            <Tooltip title="Download Word Report (.docx)">
              {wordLoading ? (
                <Spinner />
              ) : (
                <DescriptionOutlinedIcon
                  fontSize="medium"
                  className="stock-toggle-icon"
                  onClick={wordExportFn}
                  sx={{ color: '#2b579a' }}
                />
              )}
            </Tooltip>
          )}

          {otherControls &&
            otherControls.map(({ icon, fn, title, tooltip }, index) => (
              <CTooltip
                key={index}
                content={tooltip || undefined}
                className={tooltip ? '' : 'd-none'}
              >
                <div className="d-flex flex-row gap-1 align-items-center text-white">
                  {title && (
                    <p
                      className="lblAddPartner labelName heading-font-weight text-truncate m-0"
                      onClick={fn}
                    >
                      {title}
                    </p>
                  )}
                  {icon && <CIcon className="stock-toggle-icon" onClick={fn} icon={icon} />}
                </div>
              </CTooltip>
            ))}

          {!filterDisable && <FilterIconMenu />}
        </CCol>
      </CRow>
      {/* Label note — mobile/tablet only */}
      {labelNote && (
        <CRow className="w-100 p-0 d-lg-none">
          <CCol xs={12} className="text-center p-0">
            <div className="label-note-warning">{labelNote}</div>
          </CCol>
        </CRow>
      )}

      {/* ── Row 2: Search/Select — mobile only (below lg) ── */}
      {hasSearchOrSelect && (
        <CRow className="w-100 p-0 g-2 mt-1 d-lg-none">
          <CCol xs={12} className="p-0">
            <SearchSelectInputs topSearch={topSearch} topSelect={topSelect} />
          </CCol>
        </CRow>
      )}
    </div>
  );
};

export default DataGridHeader;
